import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatRupiah, formatApiError, formatTanggal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import StatsCharts from "@/components/StatsCharts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BarChart3, TrendingUp, TrendingDown } from "lucide-react";

function todayIso() { return new Date().toISOString().slice(0,10); }
function shiftDays(iso, d) { const dt=new Date(iso); dt.setDate(dt.getDate()+d); return dt.toISOString().slice(0,10); }

export default function DailyStatsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [start, setStart] = useState(shiftDays(todayIso(), -29));
  const [end, setEnd] = useState(todayIso());
  const [userId, setUserId] = useState("all");
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState({ pengajuan: 0, selesai: 0, batal: 0 });

  const load = useCallback(async () => {
    try {
      const params = { start, end };
      if (isAdmin && userId !== "all") params.user_id = userId;
      const { data } = await api.get("/stats/daily", { params });
      const map = Object.fromEntries(data.map(r => [r.date, r]));
      const out = [];
      let cur = start;
      while (cur <= end) {
        out.push(map[cur] || { date: cur, total: 0, nominal: 0, sph: 0, invoice: 0, nominal_sph: 0, nominal_invoice: 0 });
        cur = shiftDays(cur, 1);
      }
      setRows(out);
      // Fetch status breakdown for the same range using /dashboard/stats
      try {
        const sParams = { period: "custom", start, end, scope: "all" };
        const { data: sd } = await api.get("/dashboard/stats", { params: sParams });
        setStatusBreakdown({ pengajuan: sd.pengajuan || 0, selesai: sd.selesai || 0, batal: sd.batal || 0 });
      } catch { /* non-fatal */ }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [start, end, userId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
  }, [isAdmin]);

  const totals = useMemo(() => rows.reduce((a,r)=>({n:a.n+r.nominal, c:a.c+r.total, sph:a.sph+r.sph, inv:a.inv+r.invoice}),{n:0,c:0,sph:0,inv:0}), [rows]);
  const first = rows[0]?.nominal || 0;
  const last = rows[rows.length-1]?.nominal || 0;
  const trendUp = last >= first;

  return (
    <div className="space-y-6" data-testid="daily-stats-page">
      <PageHeader overline="Statistik" title="Statistik Harian" description="Visualisasi nominal & jumlah surat per hari. Filter user untuk melihat performa per marketing." icon={BarChart3} tone="brand" />

      <Card className="p-5 border-border shadow-none">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><Label>Dari</Label><DatePicker value={start} onChange={setStart} testId="ds-start" placeholder="Tanggal awal" /></div>
          <div><Label>Sampai</Label><DatePicker value={end} onChange={setEnd} testId="ds-end" placeholder="Tanggal akhir" /></div>
          {isAdmin && <div><Label>User</Label><Select value={userId} onValueChange={setUserId}><SelectTrigger data-testid="ds-user"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Semua user</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>)}</SelectContent></Select></div>}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border-border p-4"><p className="overline">Total Nominal</p><div className="mt-2 font-heading text-2xl font-bold text-ink-900">{formatRupiah(totals.n)}</div></Card>
        <Card className="border-border p-4"><p className="overline">Jumlah Surat</p><div className="mt-2 font-heading text-2xl font-bold text-ink-900">{totals.c}</div></Card>
        <Card className="border-border p-4"><p className="overline">SPH / Invoice</p><div className="mt-2 font-heading text-2xl font-bold text-ink-900">{totals.sph} <span className="text-sm text-ink-500">/ {totals.inv}</span></div></Card>
        <Card className={`border-border p-4 ${trendUp?"bg-emerald-50":"bg-rose-50"}`}><p className="overline">Tren</p><div className={`mt-2 flex items-center gap-1 font-heading text-xl font-bold ${trendUp?"text-emerald-700":"text-rose-700"}`}>{trendUp?<TrendingUp className="h-5 w-5" />:<TrendingDown className="h-5 w-5" />} {trendUp?"Naik":"Turun"}</div></Card>
      </div>

      {/* Interactive charts (Bar / Line / Area + Pies) */}
      <StatsCharts rows={rows} statusBreakdown={statusBreakdown} />

      <Card className="overflow-hidden border-border shadow-none">
        <div className="grid grid-cols-5 px-4 py-2 text-xs font-medium text-ink-500 bg-secondary/40">
          <div>Tanggal</div><div className="text-right">Surat</div><div className="text-right">SPH</div><div className="text-right">Invoice</div><div className="text-right">Nominal</div>
        </div>
        <div className="divide-y divide-border max-h-80 overflow-y-auto">
          {rows.filter(r=>r.total>0).map(r => (
            <div key={r.date} className="grid grid-cols-5 px-4 py-2 text-sm">
              <div>{formatTanggal(r.date)}</div>
              <div className="text-right">{r.total}</div>
              <div className="text-right">{r.sph}</div>
              <div className="text-right">{r.invoice}</div>
              <div className="text-right font-mono">{formatRupiah(r.nominal)}</div>
            </div>
          ))}
          {!rows.some(r=>r.total>0) && <div className="px-4 py-8 text-center text-sm text-ink-500">Tidak ada data pada rentang ini.</div>}
        </div>
      </Card>
    </div>
  );
}
